import {defineStore} from "pinia";
import {isYapiError, YapiError} from "@YenzaCT/sdk";
import yapi from "@/lib/yapi";
import {isEqual, cloneDeep} from "lodash";

const defaultState = {
  availableFilters: {
    tenants: true,
    institutions: true,
    cohorts: true,
    curriculum: false
  },
  filters: {
    tenants: [] as string[] | undefined,
    institutions: [] as string[] | undefined,
    cohorts: [] as string[] | undefined,
    curriculum: undefined as string | undefined,
    customDate: undefined as string[] | undefined,
    dateFilter: undefined as string | undefined,
    dateFilterUnit: undefined as string | undefined,
    dateFilterValue: undefined as string | string[] | undefined,
  },
  active: false,
  refreshKey: 0,
};

export const useReportingStore = defineStore("reporting", {
  state: () => ({
    availableFilters: {
      tenants: true,
      institutions: true,
      cohorts: true,
      curriculum: false
    },
    filters: {
      tenants: [] as string[] | undefined,
      institutions: [] as string[] | undefined,
      cohorts: [] as string[] | undefined,
      curriculum: undefined as string | undefined,
      customDate: undefined as string[] | undefined,
      dateFilter: undefined as string | undefined,
      dateFilterUnit: undefined as string | undefined,
      dateFilterValue: undefined as string | string[] | undefined,
    },
    active: false,
    refreshKey: 0
  }),
  actions: {
    activateAndRefresh() {
      this.active = true;
      this.refreshKey++;
    },
    setDateFilter(filter: string, value: string | string[], unit: string) {
      this.filters.dateFilter = filter;
      this.filters.dateFilterValue = value;
      this.filters.dateFilterUnit = unit;
    },
    setTenants(tenants: string[] | undefined) {
      this.filters.tenants = tenants;
    },
    setInstitutions(institutions: string[] | undefined) {
      this.filters.institutions = institutions;
    },
    setCohorts(cohorts: string[] | undefined) {
      this.filters.cohorts = cohorts;
    },
    setFiltersAndRefresh(filters: Partial<typeof defaultState.filters>) {
      // 1. Reset all filters to defaults
      this.resetFilters();
      // 2. Apply provided filters
      this.filters = cloneDeep(filters);
      // 3. Activate and refresh
      this.activateAndRefresh();
    },
    resetState() {
      this.$state = cloneDeep(defaultState);
      this.filters.dateFilter = undefined;
      this.filters.dateFilterValue = undefined;
      this.filters.dateFilterUnit = undefined;
    },
    resetFilters() {
      this.filters = cloneDeep(defaultState.filters);
      this.filters.dateFilter = undefined;
      this.filters.dateFilterValue = undefined;
      this.filters.dateFilterUnit = undefined;
    },
    setAvailableFilters(filters: Partial<typeof defaultState["availableFilters"]>) {
      this.availableFilters = cloneDeep(filters);
    },
    async fetchReport(metric: string, data: Record<string, string> | undefined = undefined) {
      try {
        const res = await yapi.admin.metric.post(metric, {
          ...this.getActiveFilters,
          ...data,
        });
        return res.data;
      } catch (e) {
        if (isYapiError(e)) {
          const yError = e as YapiError;
          throw new Error(yError.message);
        }
      }
    },
  },
  getters: {
    isCohortFilteringActive: (state) => state.availableFilters.cohorts && state.filters.cohorts && state.filters.cohorts.length > 0,
    isInstitutionFilteringActive: (state) =>
      state.availableFilters.institutions &&
      state.filters.institutions &&
      state.filters.institutions.length > 0,
    isTenantFilteringActive: (state) => state.availableFilters.tenants && state.filters.tenants && state.filters.tenants.length > 0,
    getActiveFilters: (state) => ({
      tenants: state.filters.tenants?.map((tenant) => tenant),
      institutions: state.filters.institutions?.map((institution) => institution),
      cohorts: state.filters.cohorts?.map((cohort) => cohort),
      curriculum: state.filters.curriculum,
      time: [{
        "dateFilterType": state.filters.dateFilter,
        "unit": state.filters.dateFilterUnit,
        "value": state.filters.dateFilterValue
      }],
    }),
    activeFilterCount: (state) => {
      let count = 0;
      if (state.filters.tenants && state.filters.tenants.length > 0) count++;
      if (state.filters.institutions && state.filters.institutions.length > 0) count++;
      if (state.filters.cohorts && state.filters.cohorts.length > 0) count++;
      if (state.filters.curriculum !== undefined) count++;
      if (state.filters.customDate !== undefined && state.filters.customDate.length > 0) count++;
      if (state.filters.dateFilter !== undefined) count++;
      return count;
    },
    isDateFilterActive: (state) => (filter: string, value: string | string[], unit: string) => {
      if (Array.isArray(value)) {
        return state.filters.dateFilter === filter &&
          isEqual(state.filters.dateFilterValue, value) &&
          state.filters.dateFilterUnit === unit;
      }
      return state.filters.dateFilter === filter &&
        state.filters.dateFilterValue === value &&
        state.filters.dateFilterUnit === unit;
    },
  },

});
